<script setup lang="ts">
import { Plus, EditPencil, Megaphone, SendDiagonal, Rocket, Undo, Prohibition } from "@iconoir/vue";
import { useList } from "~/composables/use-list";
import ListPage from "~/components/templates/ListPage.vue";
import ListEmpty from "~/components/templates/ListEmpty.vue";
import AppFormModal from "./AppFormModal.vue";
import AppSecretModal from "./AppSecretModal.vue";
import { AdminPermission } from "~~/schemas/admin-role";
import { AppStatus, appStatusTransitions, type AppRegistration as App } from "@artech-global/types";

defineOptions({ name: "AppList" });

const { t } = useI18n();
const { $api } = useNuxtApp();
const toast = useToast();
const alert = useAlert();
const { can } = useAccess();
const { set: setTitle } = usePageTitle();

const defaultFilter = {
  keyword: "",
  status: "all",
  page: 1,
  pageSize: 20,
};

const {
  firstLoading,
  listLoading,
  load,
  itemCount,
  pageCount,
  items,
  filter,
  isEmpty,
  isSearchEmpty,
  search,
  searchDebounced,
} = useList<App, typeof defaultFilter>({
  endpoint: "/apps",
  defaultFilter,
});

const formRef = ref<InstanceType<typeof AppFormModal> | null>(null);
const secretRef = ref<InstanceType<typeof AppSecretModal> | null>(null);

const statusOptions = computed(() => [
  { value: "all", label: t("app.status.all") },
  { value: AppStatus.Draft, label: t("app.status.draft") },
  { value: AppStatus.InReview, label: t("app.status.inReview") },
  { value: AppStatus.Upcoming, label: t("app.status.upcoming") },
  { value: AppStatus.Published, label: t("app.status.published") },
  { value: AppStatus.Delisted, label: t("app.status.delisted") },
]);

const statusIntents = {
  [AppStatus.Draft]: "neutral",
  [AppStatus.InReview]: "warning",
  [AppStatus.Upcoming]: "informative",
  [AppStatus.Published]: "positive",
  [AppStatus.Delisted]: "negative",
} as const;

const statusLabels: Record<AppStatus, string> = {
  [AppStatus.Draft]: "app.status.draft",
  [AppStatus.InReview]: "app.status.inReview",
  [AppStatus.Upcoming]: "app.status.upcoming",
  [AppStatus.Published]: "app.status.published",
  [AppStatus.Delisted]: "app.status.delisted",
};

/**
 * A transition is labelled by where it comes from as much as where it goes:
 * in-review → draft is a rejection, delisted → published is a republish.
 */
const transitionMeta: Record<string, { labelKey: string; icon: any; confirm: "none" | "warning" | "negative" }> = {
  [`${AppStatus.Draft}>${AppStatus.InReview}`]: { labelKey: "app.action.submitReview", icon: markRaw(SendDiagonal), confirm: "none" },
  [`${AppStatus.InReview}>${AppStatus.Published}`]: { labelKey: "app.action.publish", icon: markRaw(Rocket), confirm: "warning" },
  [`${AppStatus.InReview}>${AppStatus.Draft}`]: { labelKey: "app.action.reject", icon: markRaw(Undo), confirm: "none" },
  [`${AppStatus.Draft}>${AppStatus.Upcoming}`]: { labelKey: "app.action.announce", icon: markRaw(Megaphone), confirm: "none" },
  [`${AppStatus.InReview}>${AppStatus.Upcoming}`]: { labelKey: "app.action.announce", icon: markRaw(Megaphone), confirm: "none" },
  [`${AppStatus.Upcoming}>${AppStatus.Published}`]: { labelKey: "app.action.publish", icon: markRaw(Rocket), confirm: "warning" },
  [`${AppStatus.Upcoming}>${AppStatus.Draft}`]: { labelKey: "app.action.reject", icon: markRaw(Undo), confirm: "none" },
  [`${AppStatus.Published}>${AppStatus.Delisted}`]: { labelKey: "app.action.delist", icon: markRaw(Prohibition), confirm: "negative" },
  [`${AppStatus.Delisted}>${AppStatus.Published}`]: { labelKey: "app.action.republish", icon: markRaw(Rocket), confirm: "warning" },
};

function transitionsFor(row: App) {
  const from = (row.status || AppStatus.Draft) as AppStatus;
  return (appStatusTransitions[from] || []).map((to) => ({
    to,
    ...transitionMeta[`${from}>${to}`]!,
  }));
}

function openCreate() {
  formRef.value?.open();
}

function openEdit(row: App) {
  formRef.value?.open(row.id);
}

async function changeStatus(row: App, to: AppStatus, confirm: "none" | "warning" | "negative", labelKey: string) {
  if (confirm !== "none") {
    const ok = await alert[confirm]({
      title: t(`${labelKey}ConfirmTitle`),
      description: t(`${labelKey}ConfirmBody`, { name: row.name }),
      confirmLabel: t(labelKey),
      cancelLabel: t("action.cancel"),
    });
    if (!ok) return;
  }

  try {
    await $api(`/apps/${row.id}/status`, { method: "POST", body: { status: to } });
    toast.success({ title: t("message.updateSuccess") });
    await load();
  } catch (e: any) {
    toast.error({ title: e?.data?.message || t("message.updateFailed") });
  }
}

function onCreated(secrets: { clientSecret: string; signingSecret: string }) {
  secretRef.value?.open(secrets);
}

async function onSaved() {
  await load();
}

onMounted(async () => {
  setTitle(t("app.title"));
  await load();
});

watch(
  () => filter.keyword,
  () => {
    searchDebounced();
  },
);

watch(
  () => filter.status,
  () => {
    search();
  },
);

watch(
  () => filter.page,
  () => {
    load();
  },
);
</script>

<template>
  <ListPage
    :title="t('app.title')"
    :first-loading="firstLoading"
    :is-empty="isEmpty"
    :is-search-empty="isSearchEmpty"
  >
    <template #status>
      {{ listLoading ? t("ui.loading") : `${itemCount} ${t('app.itemsSuffix')}` }}
    </template>

    <template #actions>
      <Button
        v-if="can(AdminPermission.AppsWrite)"
        variant="primary"
        size="small"
        @click="openCreate"
      >
        <Plus class="icon" />
        {{ t("app.add") }}
      </Button>
    </template>

    <template #empty>
      <ListEmpty :title="t('app.emptyTitle')" :subtitle="t('app.emptySubtitle')">
        <template #action>
          <Button v-if="can(AdminPermission.AppsWrite)" variant="primary" size="small" @click="openCreate">
            <Plus class="icon" />
            {{ t("app.add") }}
          </Button>
        </template>
      </ListEmpty>
    </template>

    <template #search-empty>
      <ListEmpty
        :title="t('app.emptySearchTitle')"
        :subtitle="t('app.emptySearchSubtitle')"
      />
    </template>

    <template #filter-left>
      <FormSelect
        v-model="filter.status"
        :options="statusOptions"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #filter-right>
      <FormSearch
        v-model="filter.keyword"
        :placeholder="t('field.search')"
        size="sm"
        class="w-full md:w-1/2"
      />
    </template>

    <template #default>
      <DataTable
        :columns="[
          { key: 'name', label: t('field.name'), class: 'md:min-w-[280px]' },
          { key: 'kind', label: t('app.field.kind'), class: 'min-w-[128px] hidden md:table-cell' },
          { key: 'status', label: t('field.status'), class: 'min-w-[160px]' },
          { key: 'actions', align: 'right', label: t('ui.action'), class: 'min-w-[120px]' },
        ]"
        :items="items"
        @row-click="(row: App) => can(AdminPermission.AppsWrite) && openEdit(row)"
      >
        <template #cell-name="{ row }">
          <div class="flex items-center gap-small">
            <Avatar
              :src="row.logo?.url"
              :initials="row.name?.substring(0, 2).toUpperCase()"
              shape="square"
              size="small"
            />
            <div class="flex flex-col min-w-0">
              <span class="font-semibold truncate">{{ row.name }}</span>
              <!-- Slug + developer are reference detail: on a narrow screen the
                   name alone carries the row. -->
              <span class="text-body-small-tertiary content-neutral-medium hidden md:block truncate">
                {{ row.developerName ? `${row.slug} · ${row.developerName}` : row.slug }}
              </span>
            </div>
          </div>
        </template>

        <template #cell-kind="{ row }">
          <Badge intent="neutral" emphasis="medium" size="small">
            {{ t(`app.kind.${row.kind || "integration"}`) }}
          </Badge>
        </template>

        <template #cell-status="{ row }">
          <div class="flex items-center gap-xsmall flex-wrap">
            <Badge :intent="statusIntents[row.status] || 'neutral'" emphasis="medium" size="small">
              {{ t(statusLabels[row.status] || "app.status.draft") }}
            </Badge>
            <!-- Access is only worth a mark when it restricts something. -->
            <Badge v-if="row.accessLevel === 'private'" intent="warning" emphasis="medium" size="small">
              {{ t('app.access.privateCount', { count: row.allowedWorkspaceIDs?.length || 0 }) }}
            </Badge>
          </div>
        </template>

        <template #actions="{ row }">
          <div class="flex items-center justify-end gap-xsmall">
            <template v-if="can(AdminPermission.AppsWrite)">
              <ButtonIcon
                v-for="transition in transitionsFor(row)"
                :key="transition.to"
                variant="neutral-flat"
                size="small"
                :title="t(transition.labelKey)"
                :aria-label="t(transition.labelKey)"
                @click.stop="changeStatus(row, transition.to, transition.confirm, transition.labelKey)"
              >
                <component :is="transition.icon" class="icon" />
              </ButtonIcon>

              <ButtonIcon
                variant="neutral-flat"
                size="small"
                :title="t('action.edit')"
                :aria-label="t('action.edit')"
                @click.stop="openEdit(row)"
              >
                <EditPencil class="icon" />
              </ButtonIcon>
            </template>
          </div>
        </template>
      </DataTable>

      <Pagination
        v-if="pageCount > 1"
        v-model:page="filter.page"
        :count="itemCount"
        :page-size="filter.pageSize"
        class="mt-medium"
      />
    </template>
  </ListPage>

  <AppFormModal ref="formRef" @saved="onSaved" @created="onCreated" />
  <AppSecretModal ref="secretRef" />
</template>
